// EditSpeakUpCategoryDrawer.tsx
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Switch, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast } from 'src/components/molecules';
import {
  GetCategoryConfigCategoriesQuery,
  useGetCategoryConfigCategoriesQuery,
  useUpdateCategoryMutation,
} from 'src/graphql/generated';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'src/types';
import EditorBox from 'src/components/admin/configuration/EditorBox';
import { EditorValue } from 'src/lib/rich-text-editor';
import { buildCategoryFieldsPayload } from '../helpers/buildCategoryFieldsPayload';
import { computeEditCategoryHasUnsavedChanges } from '../helpers/computeEditCategoryHasUnsavedChanges';
import { mapCategoryToEditState } from '../helpers/mapCategoryToEditState';
import {
  AllConfigsOffModal,
  CategoryDrawerShell,
  FormUnpublishedModal,
} from '../molecules';
import { CHANNEL_FEATURE_FLAGS } from '../types';

import { DiscardChangesModal } from './DiscardChangesModal';
import { SpeakUpReportedCategoryFields } from './SpeakUpReportedCategoryFields';

const MAX_NAME_LENGTH = 140;
const DEFAULT_LOCALE = 'en-GB';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

interface EditSpeakUpCategoryDrawerProps {
  category: Category;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditSpeakUpCategoryDrawer: React.FC<
  EditSpeakUpCategoryDrawerProps
> = ({ category, isOpen, onClose, onSuccess }) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const goTogetherEnabled = useIsFeatureEnabled(FeatureFlag.GO_TOGETHER);
  const dynamicQuestionsV1 = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );
  const vaultTalkEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.vaultTalk,
  );
  const slackEntitled = useIsFeatureEnabled(CHANNEL_FEATURE_FLAGS.slack);
  const reportingPortalEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.reportingPortal,
  );

  const baseline = useMemo(() => mapCategoryToEditState(category), [category]);

  const [name, setName] = useState(baseline.name);
  const [description, setDescription] = useState<EditorValue>(
    EditorValue.createFromString(baseline.description, 'html'),
  );
  const [isDetermined, setIsDetermined] = useState(baseline.isDetermined);
  const [isReported, setIsReported] = useState(baseline.isReported);
  const [reportedFields, setReportedFields] = useState(baseline.reportedFields);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isAllOffModalOpen, setIsAllOffModalOpen] = useState(false);
  const [isFormUnpublishedModalOpen, setIsFormUnpublishedModalOpen] =
    useState(false);
  const [nameError, setNameError] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  useEffect(() => {
    setName(baseline.name);
    setDescription(EditorValue.createFromString(baseline.description, 'html'));
    setIsDetermined(baseline.isDetermined);
    setIsReported(baseline.isReported);
    setReportedFields(baseline.reportedFields);
    setIsDiscardModalOpen(false);
    setIsAllOffModalOpen(false);
    setIsFormUnpublishedModalOpen(false);
    setNameError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.id]);

  const queryClient = useQueryClient();
  const { data } = useGetCategoryConfigCategoriesQuery({ isCOI: false });
  const categories = data?.getOrganisationCategories ?? [];
  const { mutateAsync: updateCategory } = useUpdateCategoryMutation();

  const isDuplicateName = (newName: string): boolean => {
    if (!newName.trim()) return false;
    return categories.some(
      (cat) =>
        cat.id !== category.id &&
        cat.locales?.[DEFAULT_LOCALE]?.name === newName.trim(),
    );
  };

  const handleNameChange = (value: string) => {
    const trimmed = value.slice(0, MAX_NAME_LENGTH);
    setName(trimmed);
    if (nameError && !isDuplicateName(trimmed)) {
      setNameError('');
    }
  };

  const commitSave = async () => {
    if (isSavingCategory === true) return;
    setIsSavingCategory(true);
    try {
      await updateCategory({
        category: {
          id: category.id,
          ...buildCategoryFieldsPayload({
            name,
            isDetermined,
            isReported,
            reportedFields,
            entitlements: {
              goTogetherEnabled,
              vaultTalkEntitled,
              slackEntitled,
              reportingPortalEntitled,
            },
            existingDescription: description.toString('html'),
            ...(!dynamicQuestionsV1 && {
              legacyOverrides: {
                manualIntake: reportedFields.manualIntake,
              },
            }),
          }),
        },
      });
      await queryClient.refetchQueries(['GetCategoryConfigCategories']);
      setIsAllOffModalOpen(false);
      setIsFormUnpublishedModalOpen(false);
      onClose();
      onSuccess?.();
      setIsSavingCategory(false);
    } catch {
      handleErrorToast({ toastMessage: 'Error updating category' });
      setIsSavingCategory(false);
    }
  };

  const handleSave = () => {
    if (name.trim() && isDuplicateName(name.trim())) {
      setNameError(
        'Category name already exists. Please enter a unique name or modify the existing category.',
      );
      return;
    }

    const willBothBeOff = !isDetermined && !isReported;
    const reportedTurnedOff = category.isReportedCategory && !isReported;
    const wasCustom = category.isCustomQuestions;

    if (willBothBeOff) {
      setIsAllOffModalOpen(true);
      return;
    }
    if (reportedTurnedOff && wasCustom) {
      setIsFormUnpublishedModalOpen(true);
      return;
    }
    if (!isSavingCategory) {
      commitSave();
    }
  };

  const hasUnsavedChanges = computeEditCategoryHasUnsavedChanges(
    {
      name,
      description: description.toString('html'),
      isDetermined,
      isReported,
      reportedFields,
    },
    baseline,
  );

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <CategoryDrawerShell
        open={isOpen}
        title="Edit category"
        onRequestClose={handleRequestClose}
        onSave={handleSave}
        saveDisabled={!name.trim() || isSavingCategory}
        testId="edit-category-drawer"
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Category name{' '}
              <span style={{ color: color.type.muted.value }}>*</span>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            data-testid="edit-category-name-input"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={!!nameError}
          />
          {nameError && (
            <Typography
              sx={{
                fontSize: '12px',
                color: color.status.error.default.value,
                mt: '4px',
              }}
              data-testid="edit-category-name-error"
            >
              {nameError}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={isDetermined}
                onChange={(e) => setIsDetermined(e.target.checked)}
                data-testid="edit-category-determined-toggle"
                sx={{ flexShrink: 0, ml: '8px' }}
                slotProps={{
                  input: {
                    'aria-label': 'Determined category',
                    sx: { width: '100% !important', height: '100% !important' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Determined category
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: color.type.muted.value,
                ml: '8px',
              }}
            >
              This allows the category to be selected in Resolution Hub
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={isReported}
                onChange={(e) => setIsReported(e.target.checked)}
                data-testid="edit-category-reported-toggle"
                sx={{ flexShrink: 0, ml: '8px' }}
                slotProps={{
                  input: {
                    'aria-label': 'Reported category',
                    sx: { width: '100% !important', height: '100% !important' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Reported category
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: color.type.muted.value,
                ml: '8px',
              }}
            >
              This allows the category to be selected by reporters across intake
              channels
            </Typography>
          </Box>
        </Box>

        {isReported && (
          <SpeakUpReportedCategoryFields
            value={reportedFields}
            onChange={setReportedFields}
            goTogetherEnabled={goTogetherEnabled}
            mode="edit"
            hideQuestionFlowTabs={!dynamicQuestionsV1}
          />
        )}

        {isReported && reportedFields.questionFlow === 'default' && (
          <Box>
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
                display: 'block',
                mb: '4px',
              }}
            >
              Category description
            </Typography>
            <EditorBox value={description} onChange={setDescription} />
          </Box>
        )}
      </CategoryDrawerShell>

      <DiscardChangesModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={() => {
          setIsDiscardModalOpen(false);
          onClose();
        }}
      />

      <AllConfigsOffModal
        open={isAllOffModalOpen}
        onClose={() => setIsAllOffModalOpen(false)}
        onConfirm={() => void commitSave()}
      />

      <FormUnpublishedModal
        open={isFormUnpublishedModalOpen}
        onClose={() => setIsFormUnpublishedModalOpen(false)}
        onConfirm={() => void commitSave()}
      />
    </>
  );
};
